import { type ReactNode, useState } from "react";
import { useForm, FormProvider, type FieldValues, type DefaultValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@cieda/djayma-ui";
import type { ZodType } from "zod";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminFormProps<T extends FieldValues> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly schema: ZodType<T, any, any>;
  readonly defaultValues: DefaultValues<T>;
  readonly onSubmit: (data: T) => void | Promise<void>;
  readonly children: ReactNode;
  readonly submitLabel?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminForm<T extends FieldValues>({
  schema,
  defaultValues,
  onSubmit,
  children,
  submitLabel = "Enregistrer",
}: AdminFormProps<T>) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const handleSubmit = methods.handleSubmit(async (data) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {children}

        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Chargement..." : submitLabel}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
